import React from "react";
import { useTranslate } from "@refinedev/core";
import { Empty } from "antd";
import type { AddonActivation, AddonItemStat } from "types/";
import { AnalyticsCard } from "../AnalyticsCard";
import { ItemRows, type ItemRow } from "../ItemRows";
import { formatPct } from "../utils/formatPct";
import { ADDON_COLORS, type NonQuizAddonKey } from "./utils/metrics";

const T_PREFIX = "media.projects.analytics.addons";

export type AddonItemOpenRateCardProps = {
  addon: NonQuizAddonKey;
  activation: AddonActivation;
  /** The items as `mergeItems` returns them: one entry per position. */
  positions: AddonItemStat[];
  /** Extra text after an item's rate, e.g. its number of flips. */
  itemNote?: (item: AddonItemStat) => string | null;
};

/** The share of the visits that opened the add-on in which each item was opened. */
export const AddonItemOpenRateCard: React.FC<AddonItemOpenRateCardProps> = ({
  addon,
  activation,
  positions,
  itemNote,
}) => {
  const t = useTranslate();
  const color = ADDON_COLORS[addon];

  const rows: ItemRow[] = positions.map((item) => {
    const untouched = item.sessions === 0;
    // Merged wordings can count a session twice, and opens can be missed:
    // an item never reads above 100% of the visits it was seen in.
    const plays = Math.max(activation.sessions_opened, item.sessions);
    const rate = untouched ? 0 : (item.sessions / plays) * 100;
    const n = t(`${T_PREFIX}.itemNumber.${addon}`, { n: item.item_index + 1 });
    const note = itemNote?.(item) ?? null;
    return {
      key: String(item.item_index),
      label: n,
      text: item.label ?? t(`${T_PREFIX}.itemFallback`, { n }),
      muted: untouched,
      value: untouched ? (
        t(`${T_PREFIX}.${addon}.untouched`)
      ) : (
        <>
          <strong>{formatPct(rate)}</strong>
          {note == null ? "" : ` · ${note}`}
        </>
      ),
      tooltip: untouched
        ? t(`${T_PREFIX}.${addon}.untouched`)
        : t(`${T_PREFIX}.${addon}.tooltip`, {
            sessions: item.sessions,
            plays,
          }),
      bar: { percent: rate, color },
    };
  });

  return (
    <AnalyticsCard
      title={t(`${T_PREFIX}.${addon}.title`)}
      subtitle={t(`${T_PREFIX}.${addon}.subtitle`)}
    >
      {rows.length > 0 ? (
        <ItemRows rows={rows} />
      ) : (
        <Empty description={t(`${T_PREFIX}.empty.noDetail`)} />
      )}
    </AnalyticsCard>
  );
};
